import { useState } from "react";
import { Form, Button, Table, Row, Col } from "react-bootstrap";
import { AppLoader } from "../../../../Components/common/AppLoader/AppLoader";
import { MdDelete, MdEdit, MdArticle } from "react-icons/md";
import { ArticleCard } from "../../../../Components/ui/ArticleCard/ArticleCard";
import { UPLOADS_BASE } from "../../constants/api";
import { formatArticleDate, mapArticleToCardProps } from "../../utils/newsArticleHelpers";

const EMPTY_FORM = {
  article_title: "",
  context_tags: "",
  media_link: "",
  short_description: "",
  publication_name: "",
  publication_date: "",
};

export const NewsArticlesScreen = ({
  articles,
  loading,
  saving,
  onSave,
  onDelete,
}) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [referenceFiles, setReferenceFiles] = useState([]);
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [removedReferenceIds, setRemovedReferenceIds] = useState([]);
  const [removedImageIds, setRemovedImageIds] = useState([]);
  const [existingReferences, setExistingReferences] = useState([]);
  const [existingImages, setExistingImages] = useState([]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setReferenceFiles([]);
    setGalleryFiles([]);
    setEditingId(null);
    setRemovedReferenceIds([]);
    setRemovedImageIds([]);
    setExistingReferences([]);
    setExistingImages([]);
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const startEdit = (article) => {
    setEditingId(article.news_article_id);
    setForm({
      article_title: article.article_title || "",
      context_tags: article.context_tags || "",
      media_link: article.media_link || "",
      short_description: article.short_description || "",
      publication_name: article.publication_name || "",
      publication_date: article.publication_date
        ? String(article.publication_date).slice(0, 10)
        : "",
    });
    setReferenceFiles([]);
    setGalleryFiles([]);
    setRemovedReferenceIds([]);
    setRemovedImageIds([]);
    setExistingReferences(article.references || []);
    setExistingImages(article.images || []);
  };

  const toggleRemoveFile = (id, type) => {
    const setter = type === "ref" ? setRemovedReferenceIds : setRemovedImageIds;
    setter((prev) =>
      prev.includes(String(id))
        ? prev.filter((x) => x !== String(id))
        : [...prev, String(id)]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const ok = await onSave(
      form,
      referenceFiles,
      galleryFiles,
      editingId,
      removedReferenceIds,
      removedImageIds
    );
    if (ok) resetForm();
  };

  if (loading) {
    return <AppLoader message="Loading news articles…" variant="portal" />;
  }

  return (
    <div className="news-articles-page">
      <div className="em-section-card">
        <h4 className="em-section-title">
          <MdArticle className="media-gallery-title-icon" />
          News Articles
        </h4>

        <Form onSubmit={handleSubmit}>
          <Row className="em-row">
            <Form.Group as={Col} md="12" controlId="articleTitle">
              <Form.Label>News Article Title</Form.Label>
              <Form.Control
                type="text"
                value={form.article_title}
                onChange={(e) => handleChange("article_title", e.target.value)}
                placeholder="Enter article title"
                required
              />
            </Form.Group>
          </Row>

          <Row className="em-row">
            <Form.Group as={Col} md="6" controlId="contextTags">
              <Form.Label>Context</Form.Label>
              <Form.Control
                type="text"
                value={form.context_tags}
                onChange={(e) => handleChange("context_tags", e.target.value)}
                placeholder="#cardiology #healthcare"
              />
              <span className="em-field-hint">Use hashtags e.g. #cardiology #research</span>
            </Form.Group>
            <Form.Group as={Col} md="6" controlId="mediaLink">
              <Form.Label>Media Link (URL)</Form.Label>
              <Form.Control
                type="url"
                value={form.media_link}
                onChange={(e) => handleChange("media_link", e.target.value)}
                placeholder="https://..."
              />
            </Form.Group>
          </Row>

          <Row className="em-row">
            <Form.Group as={Col} md="12" controlId="shortDescription">
              <Form.Label>Short Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={form.short_description}
                onChange={(e) => handleChange("short_description", e.target.value)}
                placeholder="Brief summary of the article"
              />
            </Form.Group>
          </Row>

          <Row className="em-row">
            <Form.Group as={Col} md="6" controlId="publicationName">
              <Form.Label>Publication Name</Form.Label>
              <Form.Control
                type="text"
                value={form.publication_name}
                onChange={(e) => handleChange("publication_name", e.target.value)}
                placeholder="e.g. Times of India"
              />
            </Form.Group>
            <Form.Group as={Col} md="6" controlId="publicationDate">
              <Form.Label>Publication Date</Form.Label>
              <Form.Control
                type="date"
                value={form.publication_date}
                onChange={(e) => handleChange("publication_date", e.target.value)}
              />
            </Form.Group>
          </Row>

          <Row className="em-row">
            <Form.Group as={Col} md="6" controlId="referencePdfs">
              <Form.Label>Reference Material (PDF)</Form.Label>
              <Form.Control
                type="file"
                accept=".pdf,application/pdf"
                multiple
                className="em-file-input"
                onChange={(e) => setReferenceFiles(Array.from(e.target.files || []))}
              />
              <span className="em-field-hint">Upload one or more PDF reference documents</span>
            </Form.Group>
            <Form.Group as={Col} md="6" controlId="galleryImages">
              <Form.Label>Media Gallery</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                multiple
                className="em-file-input"
                onChange={(e) => setGalleryFiles(Array.from(e.target.files || []))}
              />
              <span className="em-field-hint">Select one or more images for the article gallery</span>
            </Form.Group>
          </Row>

          {editingId && (existingReferences.length > 0 || existingImages.length > 0) && (
            <div className="news-existing-files">
              {existingReferences.length > 0 && (
                <div className="news-existing-files__group">
                  <p className="news-existing-files__label">Existing PDFs (click to remove)</p>
                  <div className="news-existing-files__list">
                    {existingReferences.map((ref) => {
                      const marked = removedReferenceIds.includes(String(ref.reference_id));
                      return (
                        <button
                          key={ref.reference_id}
                          type="button"
                          className={`news-existing-files__item${marked ? " news-existing-files__item--removed" : ""}`}
                          onClick={() => toggleRemoveFile(ref.reference_id, "ref")}
                        >
                          {ref.file_path}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {existingImages.length > 0 && (
                <div className="news-existing-files__group">
                  <p className="news-existing-files__label">Existing images (click to remove)</p>
                  <div className="em-gallery-preview">
                    {existingImages.map((img) => {
                      const marked = removedImageIds.includes(String(img.image_id));
                      return (
                        <button
                          key={img.image_id}
                          type="button"
                          className={`em-gallery-thumb awards-existing-images__thumb${marked ? " awards-existing-images__thumb--removed" : ""}`}
                          onClick={() => toggleRemoveFile(img.image_id, "img")}
                        >
                          <img
                            src={`${UPLOADS_BASE}/uploads/${img.image_path}`}
                            alt="Article"
                          />
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {(referenceFiles.length > 0 || galleryFiles.length > 0) && (
            <div className="news-new-files-preview">
              {referenceFiles.length > 0 && (
                <p className="em-field-hint">{referenceFiles.length} PDF(s) selected</p>
              )}
              {galleryFiles.length > 0 && (
                <div className="em-gallery-preview media-gallery-selected-preview">
                  {galleryFiles.map((file) => (
                    <div key={`${file.name}-${file.lastModified}`} className="em-gallery-thumb">
                      <img src={URL.createObjectURL(file)} alt={file.name} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="media-gallery-form__actions">
            {editingId && (
              <Button type="button" variant="outline-secondary" onClick={resetForm} disabled={saving}>
                Cancel Edit
              </Button>
            )}
            <Button type="submit" variant="primary" className="btn-submit" disabled={saving}>
              {saving ? "Saving…" : editingId ? "Update Article" : "Save Article"}
            </Button>
          </div>
        </Form>
      </div>

      {articles.length > 0 && (
        <div className="em-section-card">
          <h4 className="em-section-title">Saved Articles</h4>
          <div className="hm-articles-scroll">
            <div className="hm-articles-scroll__track">
              {articles.map((article) => (
                <div key={article.news_article_id} className="hm-articles-scroll__item">
                  <ArticleCard {...mapArticleToCardProps(article)} />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="em-section-card media-gallery-table-card">
        <h4 className="em-section-title">News Articles Records</h4>
        {articles.length === 0 ? (
          <p className="hm-card-text hm-card-text--muted">No news articles added yet.</p>
        ) : (
          <div className="table-responsive">
            <Table striped bordered hover size="sm" className="media-gallery-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Title</th>
                  <th>Context</th>
                  <th>Publication</th>
                  <th>Date</th>
                  <th>PDFs</th>
                  <th>Images</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {articles.map((article) => (
                  <tr key={article.news_article_id}>
                    <td>{article.news_article_id}</td>
                    <td>{article.article_title}</td>
                    <td>{article.context_tags || "—"}</td>
                    <td>{article.publication_name || "—"}</td>
                    <td>{formatArticleDate(article.publication_date)}</td>
                    <td>{article.references?.length || 0}</td>
                    <td>{article.images?.length || 0}</td>
                    <td>
                      <div className="media-gallery-table__actions">
                        <button
                          type="button"
                          className="media-gallery-table__btn"
                          onClick={() => startEdit(article)}
                          aria-label="Edit"
                        >
                          <MdEdit />
                        </button>
                        <button
                          type="button"
                          className="media-gallery-table__btn media-gallery-table__btn--danger"
                          onClick={() => onDelete(article.news_article_id)}
                          aria-label="Delete"
                          disabled={saving}
                        >
                          <MdDelete />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewsArticlesScreen;
